/**
 * Data model for the single-entity dashboard's "Bank Balance" chart
 * (dashboards round 3), also reused verbatim by the Banking page (round 3,
 * commit 7) via components/dashboard/BankBalanceHistoryChart.jsx.
 *
 * History: GET /reports/entity/bank-balance-history (backend/services/
 * bank_account_balance_snapshots.py) -- one balance per (month, bank_name),
 * summed from per-account snapshots taken from the SAME canonical
 * bank_balance_positions predicate the Bank Balance stat card's sublines
 * use. The latest populated month's per-bank total reconciles to that card
 * by construction.
 *
 * Calendar-year axis (round 3): fixed Jan-Dec of the current year, derived
 * from `new Date()`, never hardcoded. Months with no snapshot/reconstructed
 * history (including every month after the current one) get `_total: null`
 * so the chart (a Line, not stacked bars, as of round 3) renders a gap
 * rather than a fabricated zero. This chart has no forecast of its own
 * (round 3 retires the forecast-continuation segment that used to piggyback
 * the group cash forecast here); the Net Assets chart's forecast continuation
 * now reads the group/entity cash forecast directly instead of through this
 * builder's output -- see netAssetsChart.js callers.
 *
 * Partial-coverage marker: a month whose backend "coverage" field is
 * "partial" (routes/bank_account_history.py -- some but not all of today's
 * live accounts in scope had a doc that month) carries `_total` as a real,
 * honest, but understated figure -- `_partial: true` and `_excludedAccounts`
 * (human-readable labels) let the chart flag it rather than let it pass as
 * an unqualified exact total. Same coverage/excluded_accounts shape
 * services/cash_jurisdiction_snapshots.py already established for the Cash
 * Development chart, just single-series here (one Total line, not a bucket
 * per jurisdiction).
 */

import { monthKeyToLabel, calendarYearMonthKeys } from './calendarYear';

export { calendarYearMonthKeys };

// Chart-only categorical palette, deliberately separate from BankLogo.jsx's
// bankLogoConfig brand registry: brand colors cluster by nationality
// (several French banks green, several UK banks blue/purple) and read poorly
// as chart lines. This 8-hue set is chosen purely for line-to-line
// distinctness at 2px weight on a white card -- fixed order, assigned in
// sequence, never cycled (reordering would undo the adjacent-hue separation
// the order was chosen for). Never touch bankLogoConfig for this; it still
// drives brand logos/avatars elsewhere in the app.
//
// Reserved for the banks that actually co-occur on a chart today (Revolut
// Business, BNP Paribas, RBS, NatWest, Banque BCP, Wise, Revolut, and the
// legacy combined "RBS / NatWest" label some historical accounts still
// carry) -- one unique hue each, so this active set is always maximally
// spread and never collides with the fallback tier below (disjoint hex
// values, not just disjoint array indices).
export const BANK_CHART_PALETTE = [
  '#2a78d6', // blue
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#e87ba4', // magenta
  '#eb6834', // orange
];

// Secondary, lower-priority pool: bankLogoConfig entries this group rarely
// or never runs alongside the active set above (Barclays, HSBC, Lloyds,
// Agora, Monzo, Starling), plus the hash fallback for any bank name outside
// both registries (e.g. a per-account display-name leak when no institution
// resolves, or a new/mock provider). Deliberately a DIFFERENT hex set than
// BANK_CHART_PALETTE (muted-toned, not just re-ordered) so a rare/fallback
// bank can never exactly match an active one's color even if both render
// together; the categorical set is capped at 8 by design (a 9th series is
// never a generated hue), so this tier only needs to be internally
// consistent, not CVD-optimal against the primary 8.
const BANK_CHART_FALLBACK_PALETTE = [
  '#ac7339', // muted amber-brown
  '#73ac39', // muted olive
  '#39ac73', // muted teal-green
  '#3973ac', // muted steel-blue
  '#7339ac', // muted violet
  '#ac3973', // muted rose
];

// Deliberately outside both categorical pools above (a grey, not a hue) so
// the "Unknown" bucket never reads as a first-class bank alongside real ones.
export const UNKNOWN_CHART_COLOR = '#6B7280';

// Explicit slot per known bank name, in the primary (definitely-active) tier.
const BANK_CHART_SLOT = {
  'Revolut Business': 0,
  'BNP Paribas': 1,
  RBS: 2,
  NatWest: 3,
  'Banque BCP': 4,
  Wise: 5,
  Revolut: 6,
  'RBS / NatWest': 7,
};

// Explicit slot per known bank name, in the secondary (rare) tier.
const BANK_CHART_FALLBACK_SLOT = {
  Barclays: 0,
  HSBC: 1,
  Lloyds: 2,
  Agora: 3,
  Monzo: 4,
  Starling: 5,
};

/** Deterministic string hash (djb2-ish), independent of call order/context. */
function stableHash(name) {
  var hash = 0;
  var str = String(name || '');
  for (var i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** A bank's stable chart color: the muted Unknown grey, an explicit slot in either tier, or a name-keyed hash into the fallback tier. */
function chartColorForBank(name) {
  if (name === 'Unknown') return UNKNOWN_CHART_COLOR;
  if (Object.prototype.hasOwnProperty.call(BANK_CHART_SLOT, name)) {
    return BANK_CHART_PALETTE[BANK_CHART_SLOT[name]];
  }
  if (Object.prototype.hasOwnProperty.call(BANK_CHART_FALLBACK_SLOT, name)) {
    return BANK_CHART_FALLBACK_PALETTE[BANK_CHART_FALLBACK_SLOT[name]];
  }
  return BANK_CHART_FALLBACK_PALETTE[stableHash(name) % BANK_CHART_FALLBACK_PALETTE.length];
}

/**
 * Collects the ordered set of bank names across all actual months
 * (alphabetical, so render order is also stable), and assigns each a stable
 * chart color (see chartColorForBank) that never depends on which other
 * banks happen to appear alongside it, and never on first-seen order.
 */
export function collectBankSeries(monthsData) {
  var seenSet = {};
  (monthsData || []).forEach(function (m) {
    Object.keys(m.banks || {}).forEach(function (name) { seenSet[name] = true; });
  });
  var names = Object.keys(seenSet).sort();
  return names.map(function (name) {
    return { key: name, label: name, color: chartColorForBank(name) };
  });
}

/**
 * @param {object} input
 * @param {object[]} input.monthsData - GET .../bank-balance-history "months" array (already converted to displayCurrency server-side)
 * @param {number} [input.year] - defaults to the current calendar year
 */
export function buildBankBalanceHistoryChartData(input) {
  var monthsData = input.monthsData || [];
  var year = input.year || new Date().getFullYear();
  var series = collectBankSeries(monthsData);

  var byMonth = {};
  monthsData.forEach(function (m) { byMonth[m.month] = m; });

  var rows = calendarYearMonthKeys(year).map(function (monthKey) {
    var m = byMonth[monthKey];
    if (!m) {
      var emptyRow = {
        month: monthKeyToLabel(monthKey), _monthKey: monthKey, _total: null,
        _insufficient: false, _partial: false, _excludedAccounts: [], _bankPartial: {}, _bankExcludedAccounts: {},
      };
      series.forEach(function (s) { emptyRow[s.key] = null; });
      return emptyRow;
    }
    var bankCoverage = m.bank_coverage || {};
    var bankExcludedLabels = m.bank_excluded_account_labels || {};
    var bankPartial = {};
    var bankExcludedAccounts = {};
    series.forEach(function (s) {
      bankPartial[s.key] = bankCoverage[s.key] === 'partial';
      bankExcludedAccounts[s.key] = bankExcludedLabels[s.key] || [];
    });
    var row = {
      // total null = not enough data (backend coverage "insufficient": an
      // account open that month has no balance). Drawn as a gap, never 0.
      month: monthKeyToLabel(monthKey), _monthKey: monthKey,
      _total: m.total == null ? null : (parseFloat(m.total) || 0),
      _insufficient: m.coverage === 'insufficient',
      _partial: m.coverage === 'partial',
      _excludedAccounts: m.excluded_account_labels || m.excluded_accounts || [],
      _bankPartial: bankPartial,
      _bankExcludedAccounts: bankExcludedAccounts,
    };
    series.forEach(function (s) {
      // A bank absent from m.banks this month (every one of its accounts
      // excluded, e.g. not yet started transaction history) is a real gap
      // in THAT bank's line, not a fabricated 0 -- distinct from a bank that
      // genuinely reported a balance of exactly 0 (present in m.banks with
      // that value), which must still render as a real point.
      var raw = (m.banks || {})[s.key];
      row[s.key] = raw != null ? (parseFloat(raw) || 0) : null;
    });
    return row;
  });

  return { rows: rows, series: series };
}

/** Labels ("Jan", ...) of months whose total is hidden for lack of data. */
export function insufficientMonthLabels(rows) {
  return (rows || []).filter(function (r) { return r._insufficient; }).map(function (r) { return r.month; });
}

/** Latest populated month's row (by _monthKey order), or null if none. */
export function latestPopulatedRow(rows) {
  for (var i = (rows || []).length - 1; i >= 0; i--) {
    if (rows[i]._total != null) return rows[i];
  }
  return null;
}
